import React, { useState } from 'react';
import { useAddress } from '../context/AddressContext';
import { Address } from '../types/address';
import './ProfileAddressManager.css';

const ProfileAddressManager: React.FC = () => {
  const { addresses, addAddress, updateAddress, deleteAddress, setDefaultAddress } = useAddress();
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [formData, setFormData] = useState<Partial<Address>>({
    fullName: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Nigeria',
    landmark: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const addressData = formData as Address;
    
    let result;
    if (editingAddress?._id) {
      result = await updateAddress(editingAddress._id, addressData);
    } else {
      result = await addAddress(addressData);
    }
    
    if (result.success) {
      setShowForm(false);
      setEditingAddress(null);
      setFormData({
        fullName: '',
        phone: '',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'Nigeria',
        landmark: ''
      });
    }
  };

  const handleEdit = (address: Address) => {
    setEditingAddress(address);
    setFormData(address);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      await deleteAddress(id);
    }
  };

  const handleSetDefault = async (id: string) => {
    await setDefaultAddress(id);
  };

  return (
    <div className="profile-address-manager">
      <div className="address-header">
        <h3>My Delivery Addresses</h3>
        <button onClick={() => setShowForm(!showForm)} className="add-address-btn">
          + Add New Address
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="address-form">
          <h4>{editingAddress ? 'Edit Address' : 'New Address'}</h4>
          <div className="form-row">
            <input
              type="text"
              name="fullName"
              placeholder="Full Name"
              value={formData.fullName || ''}
              onChange={handleInputChange}
              required
            />
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone || ''}
              onChange={handleInputChange}
              required
            />
          </div>
          <input
            type="text"
            name="street"
            placeholder="Street Address"
            value={formData.street || ''}
            onChange={handleInputChange}
            required
          />
          <div className="form-row">
            <input
              type="text"
              name="city"
              placeholder="City"
              value={formData.city || ''}
              onChange={handleInputChange}
              required
            />
            <input
              type="text"
              name="state"
              placeholder="State"
              value={formData.state || ''}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-row">
            <input
              type="text"
              name="postalCode"
              placeholder="Postal Code (Optional)"
              value={formData.postalCode || ''}
              onChange={handleInputChange}
            />
            <input
              type="text"
              name="landmark"
              placeholder="Landmark (Optional)"
              value={formData.landmark || ''}
              onChange={handleInputChange}
            />
          </div>
          <select name="country" value={formData.country} onChange={handleInputChange}>
            <option value="Nigeria">Nigeria</option>
            <option value="Ghana">Ghana</option>
            <option value="Kenya">Kenya</option>
            <option value="South Africa">South Africa</option>
          </select>
          <div className="form-actions">
            <button type="submit">Save Address</button>
            <button type="button" onClick={() => {
              setShowForm(false);
              setEditingAddress(null);
            }}>Cancel</button>
          </div>
        </form>
      )}

      <div className="addresses-list">
        {addresses.length === 0 ? (
          <div className="no-addresses">
            <p>No addresses saved yet.</p>
            <p className="hint">Click "Add New Address" to add your first delivery address.</p>
          </div>
        ) : (
          addresses.map(address => (
            <div key={address._id} className={`address-card ${address.isDefault ? 'default' : ''}`}>
              <div className="address-content">
                {address.isDefault && <span className="default-badge">Default</span>}
                <p className="address-name">{address.fullName}</p>
                <p className="address-phone">📞 {address.phone}</p>
                <p className="address-detail">{address.street}</p>
                <p className="address-detail">{address.city}, {address.state}</p>
                {address.landmark && <p className="address-landmark">📍 {address.landmark}</p>}
              </div>
              <div className="address-actions">
                <button onClick={() => handleEdit(address)}>✏️ Edit</button>
                <button onClick={() => handleDelete(address._id!)}>🗑️ Delete</button>
                {!address.isDefault && (
                  <button onClick={() => handleSetDefault(address._id!)}>⭐ Set Default</button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProfileAddressManager;